import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import type { Gauge } from '@neopolis/engine';

import { T } from '../textes-interface.js';

/** Une variation va-t-elle dans le bon sens pour cette jauge ? */
function estUneBonneNouvelle(jauge: Gauge, variation: number): boolean {
  return jauge.sens === 'haut_meilleur' ? variation > 0 : variation < 0;
}

/** Part de la jauge remplie, entre 0 et 1. */
function remplissage(jauge: Gauge, valeur: number): number {
  const etendue = jauge.max - jauge.min;
  return etendue === 0 ? 0 : (valeur - jauge.min) / etendue;
}

/**
 * Une jauge de la ville : icône, valeur, barre, et la variation qui vient de se
 * produire. La couleur suit le champ `sens` du contenu — vert quand la ville va
 * dans la bonne direction, quel que soit le sens de la flèche.
 */
export function Jauge({ jauge, valeur }: { jauge: Gauge; valeur: number }) {
  const precedente = useRef(valeur);
  const [variation, setVariation] = useState(0);
  const animationsReduites = useReducedMotion();

  useEffect(() => {
    const ecart = valeur - precedente.current;
    precedente.current = valeur;
    if (ecart === 0) {
      return undefined;
    }

    setVariation(ecart);
    const minuteur = setTimeout(() => setVariation(0), 2500);
    return () => clearTimeout(minuteur);
  }, [valeur]);

  const part = remplissage(jauge, valeur);
  const bonneDirection = variation !== 0 && estUneBonneNouvelle(jauge, variation);

  const tendance =
    variation === 0 ? T.jauges.stable : variation > 0 ? T.jauges.enHausse : T.jauges.enBaisse;

  return (
    <li className="flex min-h-touch flex-1 items-center gap-3 rounded-xl bg-white px-3 py-2 shadow-sm">
      <span aria-hidden="true" className="text-2xl leading-none">
        {jauge.icone}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-700">{jauge.nom}</span>

        <span
          className="mt-1 block h-2 overflow-hidden rounded-full bg-slate-200"
          role="presentation"
        >
          <motion.span
            className={`block h-full rounded-full ${
              jauge.sens === 'haut_meilleur'
                ? part >= 0.5
                  ? 'bg-emerald-500'
                  : 'bg-amber-500'
                : part <= 0.5
                  ? 'bg-emerald-500'
                  : 'bg-amber-500'
            }`}
            animate={{ width: `${Math.round(part * 100)}%` }}
            transition={animationsReduites ? { duration: 0 } : { duration: 0.6, ease: 'easeOut' }}
          />
        </span>
      </span>

      <span className="flex items-baseline gap-1">
        <span className="text-lg font-semibold tabular-nums text-slate-900">{valeur}</span>

        {variation !== 0 && (
          <motion.span
            initial={animationsReduites ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-sm font-semibold tabular-nums ${
              bonneDirection ? 'text-emerald-600' : 'text-rose-600'
            }`}
          >
            {variation > 0 ? `+${variation}` : variation}
          </motion.span>
        )}
      </span>

      <span className="sr-only">
        {jauge.nom} : {valeur}, {tendance}
      </span>
    </li>
  );
}
